import { Injectable, Signal, computed, inject } from '@angular/core';
import { FilterValue } from '@api-types/types/filter-value.type';
import { Store } from '@ngrx/store';
import {
  selectAppliedFilters,
  selectAvailableFiltersAsMap,
} from 'demand-forecasting/store/filter/filter.selectors';
import { ColoredChip } from '@core/types/chip';

@Injectable({
  providedIn: 'root',
})
export class AppliedFilterChipsService {
  private readonly store = inject(Store);

  appliedFilters = this.store.selectSignal(selectAppliedFilters);
  availableFilters = this.store.selectSignal(selectAvailableFiltersAsMap);

  appliedFilterChips: Signal<ColoredChip[]> = this.computeAppliedFilterChips();

  public computeAppliedFilterChips(
    appliedFiltersAsParam?: Map<string, FilterValue[]>,
  ): Signal<ColoredChip[]> {
    return computed(() => {
      const appliedFilters = appliedFiltersAsParam || this.appliedFilters();
      const categories = Array.from(appliedFilters.keys());
      const totalNumOfCategories = categories.length;
      const filtersWithBackground: ColoredChip[] = [];

      categories.forEach((categoryKey, index) => {
        const backgroundColor = this.getChipBackgroundColor(
          index,
          totalNumOfCategories,
        );
        appliedFilters.get(categoryKey)?.forEach((filter) => {
          filtersWithBackground.push({
            label: String(filter),
            backgroundColor,
          });
        });
      });

      // sort filters by their background color, so that filters with the least amount of same background color are shown first
      // TODO: might be quite inefficient, but it should be fine for now
      filtersWithBackground.sort((a, b) => {
        const howManyColorA = filtersWithBackground.filter(
          (filter) => filter.backgroundColor === a.backgroundColor,
        ).length;
        const howManyColorB = filtersWithBackground.filter(
          (filter) => filter.backgroundColor === b.backgroundColor,
        ).length;
        return howManyColorA - howManyColorB;
      });

      return filtersWithBackground;
    });
  }

  private getChipBackgroundColor(
    index: number,
    totalNumOfCategories: number,
  ): string {
    let newLightness;

    // if there are 3 or less categories, lightness increases by 15% for each category
    if (totalNumOfCategories <= 3) {
      const lightnessValues = [70, 85, 100];
      newLightness = lightnessValues[index];
    } else {
      // if there are more than 3 categories, increase is equally distributed
      const baseLightness = 70;
      const maxLightness = 100;

      // calc step difference for more than 3 categories
      const stepDifference =
        (maxLightness - baseLightness) / (totalNumOfCategories - 1);
      newLightness = baseLightness + stepDifference * index;
    }

    return `hsl(0, 0%, ${newLightness}%)`;
  }
}
